import React, { useEffect, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { identifyLogRocketIfConsented } from '@/lib/consent';
import { useDomainStore } from '@/hooks/useStores';
import api from '@/api';
import './user-context.css';

const UserContext = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('screens');
  const { user, authStatus } = useAuthenticator();
  const domainStore = useDomainStore();
  const location = useLocation();
  const [retryTrigger, setRetryTrigger] = useState(0);

  const isAuthenticated = authStatus === 'authenticated';
  // Don't block UI on accept-invitation route - it has its own waiting logic
  const isAcceptInvitationRoute = location.pathname.startsWith('/accept-invitation');

  useEffect(() => {
    const initializeUser = async () => {
      if (isAuthenticated && user) {
        // Only register if we haven't initialized yet (prevents duplicate calls)
        if (domainStore.user) {
          return;
        }

        domainStore.setUserInitializationState(true);

        try {
          // Register user in database via API (user attributes are fetched internally)
          const registerUserResult = await api.users.registerUser();

          if (registerUserResult.success) {
            // Successfully registered - set user data in DomainStore
            const email = registerUserResult.data.email ?? '';
            const nickname = registerUserResult.data.nickname ?? '';
            domainStore.setUser({
              id: registerUserResult.data.userId,
              personId: registerUserResult.data.personId ?? null,
              email,
              nickname,
              allowedToCreateFamily: registerUserResult.data.allowedToCreateFamily ?? false
            });
            if (import.meta.env.VITE_LOGROCKET_APP_ID) {
              identifyLogRocketIfConsented(registerUserResult.data.userId, {
                email,
                ...(nickname && { name: nickname })
              });
            }
            domainStore.setUserInitializationState(false);
          } else {
            // Registration failed - do not set user, show error with better messaging
            const errorMessage =
              registerUserResult.error.code === 'UNAUTHORIZED' || registerUserResult.error.code === 'FORBIDDEN'
                ? t('userContext.authFailed')
                : registerUserResult.error.code === 'NETWORK_ERROR'
                  ? t('userContext.networkError')
                  : registerUserResult.error.getUserMessage();

            domainStore.setUserInitializationError(errorMessage);
          }
        } catch (error) {
          // Registration error - do not set user, show error
          const errorMessage =
            error instanceof Error
              ? t('userContext.registerFailed', { error: error.message })
              : t('userContext.unexpectedError');
          domainStore.setUserInitializationError(errorMessage);
        }
      }
    };

    void initializeUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, retryTrigger]);

  // Show blocking error screen if user initialization failed
  if (domainStore.userInitializationError && !domainStore.user) {
    return (
      <div className="user-context-container">
        <div className="user-context-error">
          <h2 className="user-context-error-title">{t('userContext.registrationErrorTitle')}</h2>
          <p className="user-context-error-text">{domainStore.userInitializationError}</p>
          <button
            onClick={() => {
              domainStore.clearUserInitializationError();
              // Trigger re-initialization by incrementing retry trigger
              setRetryTrigger((prev) => prev + 1);
            }}
            className="user-context-retry-button"
          >
            {t('userContext.retryRegistration')}
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while initializing, but not on accept-invitation route
  // (that route has its own waiting logic in AcceptInvitationScreen)
  if (domainStore.isInitializingUser && !domainStore.user && !isAcceptInvitationRoute) {
    return (
      <div className="user-context-loading">
        <div className="user-context-spinner" />
        <p className="user-context-loading-text">{t('userContext.initializing')}</p>
      </div>
    );
  }

  return <>{children}</>;
};

const UserContextComponent = observer(UserContext);
UserContextComponent.displayName = 'UserContext';
export default UserContextComponent;
